import {
  TNewTicketData,
  TTicketData,
  TUpdateTicketData,
} from 'views/types/tickets';
import { $api } from './interceptor';
import { ticketsStore } from 'views/store/useTicketsStore';
import { toast } from 'App';
import { isAxiosError } from 'axios';

type TParams = {
  page?: number;
  limit?: number;
  search?: string;
};

type TMetadata = {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
};

const ticketsApi = () => {
  const getTickets = async (params: TParams = {}) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.get<{
        data: TTicketData[];
        meta: TMetadata;
      }>('/api/support-tickets', {
        params,
      });
      ticketsStore.setState({ tickets: data.data });
      return data;
    } catch (error) {
      errorHandler(error);
      ticketsStore.setState({ tickets: [] });
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const getTicketById = async (id: string) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.get<TTicketData>(
        '/api/support-tickets/' + id
      );
      ticketsStore.setState({ tickets: [data] });
      return data;
    } catch (error) {
      errorHandler(error);
      ticketsStore.setState({ tickets: [] });
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const getTicketByUserId = async (id: string) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.get<TTicketData[]>(
        '/api/support-tickets/user/' + id
      );
      ticketsStore.setState({ tickets: data });
      return data;
    } catch (error) {
      errorHandler(error);
      ticketsStore.setState({ tickets: [] });
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const getTicketByAdminId = async (id: string) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.get<TTicketData[]>(
        '/api/support-tickets/admin/' + id
      );
      ticketsStore.setState({ tickets: data });
      return data;
    } catch (error) {
      errorHandler(error);
      ticketsStore.setState({ tickets: [] });
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const createTicket = async (ticketData: TNewTicketData) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.post<TTicketData>('/api/support-tickets', {
        ...ticketData,
      });

      pushSuccessToast('Ticket created successfully');
      ticketsStore.setState(state => ({
        tickets: [data, ...(state.tickets || [])],
      }));
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const assignTicketToAdmin = async (id: string, adminId: string) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.post<TTicketData>(
        `/api/support-tickets/${id}/assign`,
        {
          adminId,
        }
      );
      pushSuccessToast('Ticket assigned successfully');
      ticketsStore.setState(state => ({
        tickets: state.tickets.map(ticket =>
          ticket.id === data.id ? data : ticket
        ),
      }));
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const escaladeTicket = async (id: string, reason: string) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.post<TTicketData>(
        `/api/support-tickets/${id}/escalate`,
        {
          reason,
        }
      );
      pushSuccessToast('Ticket escalated successfully');
      ticketsStore.setState(state => ({
        tickets: state.tickets.map(ticket =>
          ticket.id === data.id ? data : ticket
        ),
      }));
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const scheduleControlForTicket = async (
    id: string,
    scheduledTime: string
  ) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.post<TTicketData>(
        `/api/support-tickets/${id}/schedule`,
        {
          scheduledTime,
        }
      );
      pushSuccessToast('Ticket scheduled successfully');
      ticketsStore.setState({ tickets: [data] });
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const resolveTicket = async (id: string) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.post<TTicketData>(
        `/api/support-tickets/${id}/resolve`
      );
      pushSuccessToast('Ticket resolved successfully');
      ticketsStore.setState(state => ({
        tickets: state.tickets.map(ticket =>
          ticket.id === data.id ? data : ticket
        ),
      }));
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const closeTicket = async (id: string) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.post<TTicketData>(
        `/api/support-tickets/${id}/close`
      );
      pushSuccessToast('Ticket closed successfully');
      ticketsStore.setState(state => ({
        tickets: state.tickets.map(t => (t.id === data.id ? data : t)),
      }));
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const updateTicket = async (id: string, data: TUpdateTicketData) => {
    try {
      ticketsStore.setState({ loading: true });
      const response = await $api.patch<TTicketData>(
        `/api/support-tickets/${id}`,
        data
      );
      pushSuccessToast('Ticket updated successfully');
      ticketsStore.setState(state => ({
        tickets: state.tickets.map(t =>
          t.id === response.data.id ? response.data : t
        ),
      }));
      return response.data;
    } catch (error) {
      errorHandler(error);
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const deleteTicket = async (id: string) => {
    try {
      ticketsStore.setState({ loading: true });
      const { data } = await $api.delete('/api/support-tickets/' + id);
      pushSuccessToast('Ticket deleted successfully');
      ticketsStore.setState(state => ({
        tickets: state.tickets.filter(t => t.id !== id),
      }));
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      ticketsStore.setState({ loading: false });
    }
  };

  const pushSuccessToast = (message: string) => {
    toast({
      title: 'Success',
      description: message,
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const errorHandler = (error: unknown) => {
    console.error(error);

    let description = 'Something went wrong. Please try again later.';

    if (isAxiosError(error)) {
      const responseMessage = error.response?.data?.message;

      if (typeof responseMessage === 'string') {
        description = responseMessage;
      } else if (Array.isArray(responseMessage)) {
        description = responseMessage.join(', ');
      }
    }

    if (description.includes('create_support_ticket_cta:create')) {
      description =
        'You do not have permission to create support tickets. Please contact your administrator.';
    } else if (
      description.includes('Access denied') &&
      description.includes('permission')
    ) {
      description =
        'You do not have the necessary permissions for this action. Please contact your administrator.';
    }

    toast({
      title: 'Error',
      description,
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  return {
    getTickets,
    getTicketById,
    getTicketByUserId,
    createTicket,
    assignTicketToAdmin,
    escaladeTicket,
    scheduleControlForTicket,
    resolveTicket,
    closeTicket,
    updateTicket,
    deleteTicket,
    getTicketByAdminId,
  };
};

export const ticketsService = ticketsApi();
